/** Single CCS Insight article — /insights/:id. */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Linkedin, Facebook, Mail } from "lucide-react";
import api from "@/lib/api";
import { InsightsHeader } from "@/pages/InsightsPage";
import { formatSpotlightDate } from "@/components/spotlight/spotlightUtils";
import SignatureBlock from "@/components/brand/SignatureBlock";
import LegalFooter from "@/components/brand/LegalFooter";
import RichText from "@/components/RichText";

const ShareRow = ({ insight }) => {
  const url = encodeURIComponent(`${window.location.origin}/insights/${insight.id}`);
  const title = encodeURIComponent(`CCS Insights — ${insight.title}`);
  const cls =
    "w-10 h-10 rounded-full border border-[#2A1810]/15 flex items-center justify-center text-[#5D3A1F] hover:border-[#C28A2E] hover:text-[#C28A2E] transition-colors duration-300";
  return (
    <div className="flex items-center gap-3" data-testid="insight-share-row">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B5944] mr-1">Share</span>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className={cls} data-testid="insight-share-linkedin"><Linkedin size={16} strokeWidth={1.5} /></a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className={cls} data-testid="insight-share-facebook"><Facebook size={16} strokeWidth={1.5} /></a>
      <a href={`mailto:?subject=${title}&body=${url}`} aria-label="Share by email" className={cls} data-testid="insight-share-email"><Mail size={16} strokeWidth={1.5} /></a>
    </div>
  );
};

export default function InsightArticlePage() {
  const { insightId } = useParams();
  const [insight, setInsight] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/public/insights/${insightId}`)
      .then((r) => {
        setInsight(r.data);
        setStatus("ready");
        window.scrollTo(0, 0);
      })
      .catch(() => setStatus("notfound"));
  }, [insightId]);

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="insight-article-page">
      <InsightsHeader />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-[#6B5944] hover:text-[#5D3A1F] mb-10 transition-colors" data-testid="back-to-insights">
          <ArrowLeft size={15} strokeWidth={1.5} /> All Insights
        </Link>

        {status === "loading" && <div className="py-24 text-center text-[#6B5944]">Loading…</div>}
        {status === "notfound" && (
          <div className="py-24 text-center" data-testid="insight-not-found">
            <p className="text-[#6B5944]">This insight could not be found.</p>
            <Link to="/insights" className="inline-block mt-4 text-[#C28A2E] font-semibold hover:underline">Browse all Insights</Link>
          </div>
        )}
        {status === "ready" && insight && (
          <article data-testid="insight-article">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#C28A2E]">{insight.category}</div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-[#2A1810]" data-testid="insight-title">
              {insight.title}
            </h1>
            <div className="mt-5 flex items-center gap-4 text-xs text-[#6B5944]">
              <span>By Joylance Chapman-George</span>
              <span className="w-1 h-1 rounded-full bg-[#C28A2E]" />
              <span>{formatSpotlightDate(insight.published_at)}</span>
              <span className="w-1 h-1 rounded-full bg-[#C28A2E]" />
              <span className="inline-flex items-center gap-1.5"><Clock size={13} strokeWidth={1.5} /> {insight.reading_time} min read</span>
            </div>
            {insight.image_url && (
              <img src={insight.image_url} alt={insight.title} className="mt-10 block w-auto max-w-full h-auto max-h-[80vh] mx-auto rounded-sm border border-[#2A1810]/5" />
            )}
            <RichText
              content={insight.content}
              className="mt-10 text-base sm:text-lg leading-[1.9] text-[#2A1810]/85"
              testid="insight-content"
            />
            <SignatureBlock className="mt-14" />
            <div className="mt-14 pt-10 border-t border-[#EBE3D2] text-center" data-testid="insight-cta-block">
              <p className="text-sm sm:text-base text-[#5D3A1F]">Ready to strengthen how your team serves customers?</p>
              <Link
                to="/book#booking"
                className="mt-5 inline-flex items-center gap-2 bg-[#C28A2E] text-[#2A1810] px-8 py-3.5 rounded-full text-sm font-bold tracking-wide hover:bg-[#E7B54D] transition-colors duration-300"
                data-testid="insight-book-cta"
              >
                Book a free discovery call <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[#EBE3D2]">
              <ShareRow insight={insight} />
            </div>
          </article>
        )}
      </main>
      <footer className="border-t border-[#EBE3D2] py-10 px-6 text-center">
        <p className="text-xs italic text-[#8B5A2B]">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
        <LegalFooter className="mt-6" />
      </footer>
    </div>
  );
}
